/* eslint-disable no-restricted-syntax */
// import { LatLng, icon } from 'leaflet';
import moment from 'moment';

import { generateTimestampsInSeconds } from '@/util/date';

import AssetSvc from '@/services/AssetSvc';
import PulseSvc from '@/services/PulseSvc';
import SummarySvc from '@/services/SummarySvc';
import DeviceSvc from '@/services/DeviceSvc';
// eslint-disable-next-line no-unused-vars
import LandmarkSvc from '@/services/LandmarkSvc';

import { buildIconConfig } from '@/util/shipIcons';

import {
  getAllVisibilityStatuses,
  saveVisibilityStatus,
  saveAllVisibilityStatuses,
} from '@/services/dbService';

const state = {
  assets: [],
  liveTrackingId: null,
  isLiveTrackingActive: false,
};

// Tipe sensor "kesehatan" yang menentukan status no-sensor (selain GPS).
// Aset hanya dianggap bermasalah bila sensor yang MEMANG dimilikinya diam.
const HEALTH_SENSOR_TYPES = ['rpm', 'flowmeter', 'ae', 'temperature'];

function convertDecimalToDMS(decimal, isLatitude) {
  const degrees = Math.floor(Math.abs(decimal));
  const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = Math.round((minutesDecimal - minutes) * 60);

  // eslint-disable-next-line no-nested-ternary
  const direction = decimal >= 0
    ? (isLatitude ? 'N' : 'E')
    : (isLatitude ? 'S' : 'W');

  return `${degrees}° ${minutes}' ${seconds}'' ${direction}`;
}

const processSummarySeriesData = (summary, firstQueryTimestamp, lastQueryTimestamp) => {
  // Bucket logic:
  // We generate a list of timestamps (buckets) between the requested start and end
  // using a fixed interval (3600 seconds = 1 hour). Server returns aggregated
  // data keyed by timestamp; we round those keys into the same hourly buckets
  // so each bucket represents data for that hour.
  const timestamps = generateTimestampsInSeconds(firstQueryTimestamp, lastQueryTimestamp, 3600);

  const roundedData = (data) => {
    const rounded = {};
    Object.keys(data || {}).forEach((key) => {
      // Round raw timestamps into the hourly bucket they belong to
      const roundedKey = Math.round(key / 3600) * 3600;
      rounded[roundedKey] = data[key];
    });
    return rounded;
  };

  const roundedGps = roundedData(summary.gps?.data);
  const roundedRpm = roundedData(summary.rpm?.data);
  const roundedFm = roundedData(summary.flowmeter?.data);
  const roundedAe = roundedData(summary.ae?.data);

  return timestamps.map((timestamp) => {
    const gpsData = roundedGps[timestamp] || {};
    const rpmData = roundedRpm[timestamp] || {};
    const fmData = roundedFm[timestamp] || {};
    const aeData = roundedAe[timestamp] || {};

    const latLong = [gpsData?.additional?.LAST_dlgpLatitude_4, gpsData?.additional?.LAST_dlgpLongitude_5];
    const totalCruise = gpsData?.additional?.SUM_dlgpDistance_3;
    const avgSpeed = gpsData.speed;
    const currentAe = Object.values(aeData).find((v) => v.rpm > 0)?.no + 1;
    const portRpm = rpmData.PORT?.rpm;
    const starboardRpm = rpmData.STARBOARD?.rpm;
    const centerRpm = rpmData.CENTER?.rpm;
    const portInFlowrate = fmData.PORT_Flow;
    const starboardInFlowrate = fmData.STARBOARD_Flow;

    return {
      gpsData,
      rpmData,
      fmData,
      aeData,

      dateTime: moment.unix(timestamp).format('DD-MM-YYYY HH:mm:ss'),
      timestamp,
      latLong,
      heading: gpsData.track,
      totalCruise,
      avgSpeed,
      currentAe,
      portRpm,
      starboardRpm,
      centerRpm,
      portInFlowrate,
      starboardInFlowrate,
    };
  });
};

const mapGpsPulseToAssets = async (currAssets, massDevicesMap, gpsPulseDatas, currTime, mode = 'standard') => Promise.all(
  currAssets.map(async (asset) => {
    const newAsset = { ...asset };
    const assetDevices = massDevicesMap.get(asset.massId) || [];
    newAsset.isVisible = true;
    newAsset.nearestLandmark = null;

    // sat mode: Use massSateliteId to match GPS data
    const isSatMode = mode === 'sat';
    let gpsData;

    if (isSatMode) {
      // Match by massSateliteId
      gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === asset.massSateliteId?.toLowerCase());
    } else {
      // Standard mode: Match by device mapping
      const gpsDevice = assetDevices.find((v) => v.devcType === 'gps');
      if (gpsDevice) {
        gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === gpsDevice.devcUniqueId.toLowerCase());
      }
    }

    if (gpsData) {
      const gpsDiffTime = parseInt(currTime) - parseInt(gpsData.timestamp);
      newAsset.trail = false;
      newAsset.dotTrail = true;
      newAsset.lastGpsData = gpsData;
      newAsset.lastGpsDataPulse = gpsData;
      newAsset.lastCoordinate = `${convertDecimalToDMS(gpsData.latitude, true)}, ${convertDecimalToDMS(gpsData.longitude, false)}`;
      // eslint-disable-next-line no-undef
      newAsset.lastLatLng = L.latLng(gpsData.latitude, gpsData.longitude);
      newAsset.dataStatus = gpsDiffTime < 600;
      newAsset.gpsDiffTime = gpsDiffTime; // Store GPS diffTime for later use

      // Determine ship status: online, offline, no-plc (GPS ok but no PLC data)
      // Default to online/offline based on GPS recency, will be updated later if no PLC
      const isGpsOnline = gpsDiffTime < 600;
      newAsset.shipStatus = isGpsOnline ? 'online' : 'offline';

      const { iconConfig, markerIconUrl } = buildIconConfig(asset, newAsset.shipStatus);
      newAsset.iconConfig = iconConfig;
      newAsset.markerIconUrl = markerIconUrl;

      try {
        const landmarkResponse = await LandmarkSvc.landmarks.nearest({
          latitude: gpsData.latitude,
          longitude: gpsData.longitude,
        });
        const landmarkData = landmarkResponse?.data?.data;
        if (landmarkData?.lndmName) {
          newAsset.nearestLandmark = {
            name: landmarkData.lndmName,
            distanceMeters: landmarkData.distanceMeters,
          };
        }
      } catch (error) {
        console.warn('Failed to fetch nearest landmark:', error);
      }
    } else {
      // No GPS data - set status to no-gps
      newAsset.shipStatus = 'no-gps';
      newAsset.dataStatus = false;

      const { iconConfig, markerIconUrl } = buildIconConfig(asset, 'no-gps');
      newAsset.iconConfig = iconConfig;
      newAsset.markerIconUrl = markerIconUrl;

      console.warn(`No GPS data found for asset: ${asset.massId}`);
    }
    return newAsset;
  }),
);

const mapOtherPulseToAssets = (
  assetsWithGps,
  massDevicesMap,
  aePulseDatas,
  rpmPulseDatas,
  fmPulseDatas,
  temperaturePulseDatas,
  powermeterPulseDatas,
  currTime,
  isSatMode = false,
) => assetsWithGps.map((asset) => {
  const newAsset = { ...asset };
  const assetDevices = massDevicesMap.get(asset.massId) || [];

  // sat mode: Use massSateliteId to match AE data
  if (isSatMode && asset.massSateliteId) {
    const aeData = aePulseDatas.filter((d) => d.deviceId === asset.massSateliteId);
    if (aeData && aeData.length > 0) newAsset.lastAeData = aeData;
  } else {
    // Standard mode: Use device mapping
    const aeDevice = assetDevices.find((v) => v.devcType === 'ae');
    if (aeDevice) {
      const aeData = aePulseDatas.filter((d) => d.deviceId === aeDevice.devcUniqueId);
      if (aeData && aeData.length > 0) newAsset.lastAeData = aeData;
    }
  }

  const rpmDevice = assetDevices.find((v) => v.devcType === 'rpm');
  if (rpmDevice) {
    const rpmData = rpmPulseDatas.filter((d) => d.deviceId === rpmDevice.devcUniqueId);
    if (rpmData && rpmData.length > 0) {
      newAsset.lastRpmData = rpmData;
    }
  }

  const fmDeviceIds = assetDevices.filter((v) => v.devcType === 'flowmeter').map((v) => v.devcUniqueId);
  if (fmDeviceIds.length > 0) {
    const fmData = fmPulseDatas.filter((d) => fmDeviceIds.includes(d.deviceId));
    if (fmData && fmData.length > 0) newAsset.lastFmData = fmData;
  }

  const temperatureDeviceIds = assetDevices.filter((v) => v.devcType === 'temperature').map((v) => v.devcUniqueId);
  if (temperatureDeviceIds.length > 0) {
    const temperatureData = temperaturePulseDatas.filter((d) => temperatureDeviceIds.includes(d.deviceId));
    if (temperatureData && temperatureData.length > 0) newAsset.lastTemperatureData = temperatureData;
  }

  const powermeterDeviceIds = assetDevices.filter((v) => v.devcType === 'powermeter').map((v) => v.devcUniqueId);
  if (powermeterDeviceIds.length > 0) {
    const powermeterData = powermeterPulseDatas.filter((d) => powermeterDeviceIds.includes(d.deviceId));
    if (powermeterData && powermeterData.length > 0) newAsset.lastPowermeterData = powermeterData;
  }

  // --- Status kesehatan sensor (generalized) ---
  // Tahap GPS sudah menetapkan 'online'/'offline'. Di sini status ditimpa
  // berdasarkan sensor yang MEMANG dimiliki aset (rpm/flowmeter/ae/temperature):
  //  - GPS segar + >=1 sensor yang diharapkan hilang/basi -> no-sensor (oranye)
  //  - GPS basi  + >=1 sensor masih segar -> no-gps (masalah GPS diprioritaskan)
  // Aset tanpa sensor kesehatan sama sekali (mis. hanya GPS) tetap online.
  const expectedHealthSensors = [...new Set(
    assetDevices.map((d) => d.devcType).filter((t) => HEALTH_SENSOR_TYPES.includes(t)),
  )];

  const isSensorFresh = (dataArr) => {
    if (!dataArr || dataArr.length === 0) return false;
    const latest = Math.max(...dataArr.map((d) => d.timestamp || 0));
    return (currTime - latest) < 600;
  };

  const sensorFreshness = {
    rpm: isSensorFresh(newAsset.lastRpmData),
    flowmeter: isSensorFresh(newAsset.lastFmData),
    ae: isSensorFresh(newAsset.lastAeData),
    temperature: isSensorFresh(newAsset.lastTemperatureData),
  };

  const isGpsOnline = newAsset.gpsDiffTime < 600;
  const hasExpectedSensor = expectedHealthSensors.length > 0;
  const allExpectedFresh = expectedHealthSensors.every((t) => sensorFreshness[t]);
  const anyExpectedFresh = expectedHealthSensors.some((t) => sensorFreshness[t]);

  if (isGpsOnline && hasExpectedSensor && !allExpectedFresh) {
    newAsset.shipStatus = 'no-sensor';
    const { iconConfig, markerIconUrl } = buildIconConfig(newAsset, 'no-sensor');
    newAsset.iconConfig = iconConfig;
    newAsset.markerIconUrl = markerIconUrl;
  } else if (!isGpsOnline && hasExpectedSensor && anyExpectedFresh) {
    newAsset.shipStatus = 'no-gps';
    const { iconConfig, markerIconUrl } = buildIconConfig(newAsset, 'no-gps');
    newAsset.iconConfig = iconConfig;
    newAsset.markerIconUrl = markerIconUrl;
  }

  return newAsset;
});

function getDeviceStatus(devices) {
  if (!devices || devices.length === 0) {
    return {
      hasExhaustTemperature: false,
      flowmeterType: 'none',
      flowmeterCount: 0,
      flowmeterPositions: [],
      flowmeterPositionCounts: {
        PORT: 0,
        STARBOARD: 0,
        CENTER: 0,
      },
      hasPowermeter: false,
      powermeterCount: 0,
      aeCount: 0,
      rpmTypes: [],
      rpmCount: 0,
      hasRpm: false,
      hasFlowmeter: false,
      hasAe: false,
      hasTemperature: false,
      healthSensors: [],
    };
  }

  // Check for exhaust temperature sensor
  const hasExhaustTemperature = devices.some(
    (d) => d.devcType === 'temperature' && d.devcLabel === 'EXHAUST_TEMPERATURE',
  );

  // Analyze flowmeters
  const flowmeters = devices.filter((d) => d.devcType === 'flowmeter');
  const flowmeterCount = flowmeters.length;

  let flowmeterType = 'none';
  const flowmeterPositionCounts = {
    PORT: 0,
    STARBOARD: 0,
    CENTER: 0,
  };

  if (flowmeterCount > 0) {
    const hasIn = flowmeters.some((d) => d.devcLabel.includes('_IN'));
    const hasOut = flowmeters.some((d) => d.devcLabel.includes('_OUT'));

    if (hasIn && hasOut) {
      flowmeterType = 'in-out';
    } else if (hasIn) {
      flowmeterType = 'in-only';
    }
  }

  // Get flowmeter positions and count them
  flowmeters.forEach((fm) => {
    if (fm.devcLabel.startsWith('PORT')) {
      flowmeterPositionCounts.PORT += 1;
    } else if (fm.devcLabel.startsWith('STARBOARD')) {
      flowmeterPositionCounts.STARBOARD += 1;
    } else if (fm.devcLabel.startsWith('CENTER')) {
      flowmeterPositionCounts.CENTER += 1;
    }
  });

  const flowmeterPositions = Object.keys(flowmeterPositionCounts).filter(
    (pos) => flowmeterPositionCounts[pos] > 0,
  );

  // Analyze powermeters
  const powermeters = devices.filter((d) => d.devcType === 'powermeter');
  const powermeterCount = powermeters.length;
  const hasPowermeter = powermeterCount > 0;

  // Kapabilitas sensor per-aset (dari device list) untuk status no-sensor.
  const hasRpm = devices.some((d) => d.devcType === 'rpm');
  const hasAe = devices.some((d) => d.devcType === 'ae');
  const hasTemperature = devices.some((d) => d.devcType === 'temperature');
  const hasFlowmeter = flowmeterCount > 0;
  const sensorPresence = {
    rpm: hasRpm,
    flowmeter: hasFlowmeter,
    ae: hasAe,
    temperature: hasTemperature,
  };
  const healthSensors = HEALTH_SENSOR_TYPES.filter((type) => sensorPresence[type]);

  // aeCount dan rpmTypes diderive setelah getLastData — lihat deriveDeviceStatusFromPulse()
  return {
    hasExhaustTemperature,
    flowmeterType,
    flowmeterCount,
    flowmeterPositions,
    flowmeterPositionCounts,
    hasPowermeter,
    powermeterCount,
    aeCount: 0,
    rpmTypes: [],
    rpmTypesCount: 0,
    hasRpm,
    hasFlowmeter,
    hasAe,
    hasTemperature,
    healthSensors,
  };
}

const singleEngineIds = [6, 97];

function deriveDeviceStatusFromPulse(assets) {
  return assets.map((asset) => {
    const deviceStatus = { ...(asset.deviceStatus || {}) };

    if (asset.lastAeData && asset.lastAeData.length > 0) {
      const maxNo = asset.lastAeData.reduce((max, item) => (item.no > max ? item.no : max), -1);
      deviceStatus.aeCount = maxNo > -1 ? maxNo + 1 : 0;
    }

    const rpmDevice = (asset.devices || []).find((d) => d.devcType === 'rpm');
    if (rpmDevice) {
      if (singleEngineIds.includes(Number(rpmDevice.devcMassId))) {
        deviceStatus.rpmTypes = ['CENTER'];
        deviceStatus.rpmTypesCount = 1;
      } else if (asset.lastRpmData && asset.lastRpmData.length > 0) {
        const uniqueRpmTypes = new Set(asset.lastRpmData.map((item) => item.type));
        deviceStatus.rpmTypes = Array.from(uniqueRpmTypes);
        deviceStatus.rpmTypesCount = deviceStatus.rpmTypes.length;
      }
    }

    return { ...asset, deviceStatus };
  });
}

const mutations = {
  UPDATE_ASSET_STATUS(state, { id, status }) {
    const asset = state.assets.find((asset) => asset.id === id);
    if (asset) {
      asset.status = status;
    }
  },
  UPDATE_ASSET_VISIBILITY(state, { massId, isVisible }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.isVisible = isVisible;
    }
  },
  SET_ALL_ASSETS_VISIBILITY(state, isVisible) {
    state.assets.forEach((asset) => {
      asset.isVisible = isVisible;
    });
  },
  UPDATE_ASSET_TRAIL(state, { massId, trail }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.trail = trail;
    }
  },
  SET_ASSETS(state, assets) {
    state.assets = assets;
  },
  SET_LIVE_TRACKING_ID(state, id) {
    state.liveTrackingId = id;
  },
  SET_LIVE_TRACKING_ACTIVE(state, status) {
    state.isLiveTrackingActive = status;
  },
};

const actions = {
  async getAssetsData({ commit }) {
    const res = await AssetSvc.getAssets();
    const assetsData = res?.data?.data ?? [];

    const processedAssets = assetsData.map((asset) => {
      const newAsset = { ...asset };
      if (newAsset.massDevice) {
        newAsset.devices = newAsset.massDevice;
        delete newAsset.massDevice;
      } else {
        newAsset.devices = [];
      }
      return newAsset;
    });

    commit('SET_ASSETS', processedAssets);
    return processedAssets;
  },

  async getAssetsDevices({ commit, state }) {
    const currAssets = Object.values(state.assets);

    const finalAssetsPromises = currAssets.map(async (asset) => {
      let deviceData = asset.devices || [];

      // If devices are missing, fetch them ONCE.
      if (deviceData.length === 0) {
        try {
          const param = { findField: 'devcMassId', findValue: asset.massId, max: 100 };
          const response = await DeviceSvc.getDevicesByAssetId(param);
          deviceData = response?.data?.data || [];
        } catch (error) {
          console.error(`Failed to fetch devices for asset ${asset.massId}`, error);
          deviceData = []; // Ensure it's an array on failure
        }
      }

      // Now, with the final deviceData (either from master or fetched), calculate status.
      const deviceStatus = await getDeviceStatus(deviceData);

      return {
        ...asset,
        devices: deviceData, // Update with fetched data if it was missing
        deviceStatus,
      };
    });

    const finalAssets = await Promise.all(finalAssetsPromises);

    commit('SET_ASSETS', finalAssets);
    // console.log('[asset.js] Devices processed and status calculated:', finalAssets);
    return finalAssets;
  },

  stopLiveTracking({ commit, state }) {
    if (state.liveTrackingId) {
      clearTimeout(state.liveTrackingId);
      commit('SET_LIVE_TRACKING_ID', null);
    }
    commit('SET_LIVE_TRACKING_ACTIVE', false);
    // Live tracking stopped (log removed)
  },

  async getLastData({ commit, state, dispatch }, mode = 'standard') {
    dispatch('stopLiveTracking');
    if (!state.isLiveTrackingActive) {
      commit('SET_LIVE_TRACKING_ACTIVE', true);
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      if (!state.isLiveTrackingActive) {
        resolve();
        return;
      }

      const currTime = Math.floor(Date.now() / 1000);
      const currAssets = Object.values(state.assets);
      // Fetching last data for assets (debug log removed)
      // console.log('[getLastData] Current assets:', JSON.stringify(currAssets, null, 2));

      const isSatMode = mode === 'sat';

      // --- sat Mode: Only fetch GPS and RPM using massSateliteId ---
      if (isSatMode) {
        // Get satellite IDs from assets
        const satelliteGpsIds = currAssets
          .filter((asset) => asset.massSateliteId)
          .map((asset) => asset.massSateliteId);

        const satelliteRpmIds = currAssets
          .filter((asset) => asset.massSateliteId)
          .map((asset) => asset.massSateliteId);

        // Fetch GPS data
        const gpsPulseDatas = satelliteGpsIds.length > 0
          ? await PulseSvc.getPulse(satelliteGpsIds, 'gps', 'sat')
          : [];

        // Fetch RPM data
        const rpmPulseDatas = satelliteRpmIds.length > 0
          ? await PulseSvc.getPulse(satelliteRpmIds, 'rpm', 'sat')
          : [];

        // Fetch AE data
        const aePulseDatas = satelliteRpmIds.length > 0
          ? await PulseSvc.getPulse(satelliteRpmIds, 'ae', 'sat')
          : [];

        // Map GPS data to assets (pass 'sat' mode)
        const assetsWithGps = await mapGpsPulseToAssets(currAssets, new Map(), gpsPulseDatas, currTime, 'sat');

        // Map RPM and AE data to assets (pass isSatMode = true)
        const finalMappedAssets = mapOtherPulseToAssets(
          assetsWithGps,
          new Map(),
          rpmPulseDatas,
          aePulseDatas,
          [], // fmPulseDatas
          [], // temperaturePulseDatas
          [], // powermeterPulseDatas
          currTime,
          true, // isSatMode
        );

        commit('SET_ASSETS', deriveDeviceStatusFromPulse(finalMappedAssets));

        // DISABLED: Live tracking recursive call (temporary)
        // if (state.isLiveTrackingActive) {
        //   const now = new Date();
        //   const nextTarget = new Date(now);
        //   nextTarget.setSeconds(59);
        //   nextTarget.setMilliseconds(0);
        //   if (now.getTime() >= nextTarget.getTime()) {
        //     nextTarget.setMinutes(nextTarget.getMinutes() + 1);
        //   }

        //   const delayMs = nextTarget.getTime() - now.getTime();

        //   const timeoutId = setTimeout(() => {
        //     dispatch('getLastData', mode);
        //   }, delayMs);

        //   commit('SET_LIVE_TRACKING_ID', timeoutId);
        //   resolve();
        // } else {
        //   console.log('[getLastData] Live tracking stopped, recursion ended.');
        //   resolve();
        // }
        resolve();
        return;
      }

      // --- Standard Mode: Full device mapping and all data types ---
      const massDevicesMap = new Map();
      currAssets.forEach((asset) => {
        massDevicesMap.set(asset.massId, asset.devices || []);
      });

      // --- Stage 1: GPS ---
      const gpsIds = currAssets.flatMap((asset) => (asset.devices || []).filter((d) => d.devcType === 'gps').map((d) => d.devcUniqueId));
      const gpsPulseDatas = gpsIds.length > 0 ? await PulseSvc.getPulse(gpsIds, 'gps', mode) : [];

      const assetsWithGps = await mapGpsPulseToAssets(currAssets, massDevicesMap, gpsPulseDatas, currTime, mode);

      commit('SET_ASSETS', assetsWithGps);

      if (!state.isLiveTrackingActive) return;

      // --- Stage 2: Other Devices ---
      const otherDevicesByType = new Map();
      const excludedPulseTypes = ['gps', 'power'];
      currAssets.forEach((asset) => {
        (asset.devices || []).forEach((device) => {
          if (!excludedPulseTypes.includes(device.devcType)) {
            if (!otherDevicesByType.has(device.devcType)) {
              otherDevicesByType.set(device.devcType, []);
            }
            otherDevicesByType.get(device.devcType).push(device.devcUniqueId);
          }
        });
      });

      const pulsePromises = [];
      const types = [];
      for (const [type, ids] of otherDevicesByType.entries()) {
        const uniqueIds = [...new Set(ids)];
        if (uniqueIds.length > 0) {
          types.push(type);
          pulsePromises.push(PulseSvc.getPulse(uniqueIds, type));
        }
      }

      const pulseResults = await Promise.all(pulsePromises);

      const pulseDataMap = new Map();
      pulseResults.forEach((result, index) => {
        pulseDataMap.set(types[index], result);
      });

      const aePulseDatas = pulseDataMap.get('ae') || [];
      const rpmPulseDatas = pulseDataMap.get('rpm') || [];
      const fmPulseDatas = pulseDataMap.get('flowmeter') || [];
      const temperaturePulseDatas = pulseDataMap.get('temperature') || [];
      const powermeterPulseDatas = pulseDataMap.get('powermeter') || [];

      const finalMappedAssets = mapOtherPulseToAssets(
        assetsWithGps,
        massDevicesMap,
        aePulseDatas,
        rpmPulseDatas,
        fmPulseDatas,
        temperaturePulseDatas,
        powermeterPulseDatas,
        currTime,
        false, // isSatMode
      );

      commit('SET_ASSETS', deriveDeviceStatusFromPulse(finalMappedAssets));

      // DISABLED: Live tracking recursive call (temporary)
      // if (state.isLiveTrackingActive) {
      //   const now = new Date();
      //   const nextTarget = new Date(now);
      //   nextTarget.setSeconds(59);
      //   nextTarget.setMilliseconds(0);
      //   if (now.getTime() >= nextTarget.getTime()) {
      //     nextTarget.setMinutes(nextTarget.getMinutes() + 1);
      //   }

      //   const delayMs = nextTarget.getTime() - now.getTime();

      //   const timeoutId = setTimeout(() => {
      //     dispatch('getLastData', mode);
      //   }, delayMs);

      //   commit('SET_LIVE_TRACKING_ID', timeoutId);
      //   resolve();
      // } else {
      //   console.log('[getLastData] Live tracking stopped, recursion ended.');
      //   resolve();
      // }
      resolve();
    });
  },

  async fetchSummary({ commit, state }, {
    start, end, interval, massId, splitByWeek = false,
  }) {
    const assets = Object.values(state.assets);

    const firstQueryTimestamp = moment(start).unix();
    const lastQueryTimestamp = moment(end).unix();

    const currentAsset = assets.find((asset) => {
      const match = String(asset.massId) === String(massId);
      return match;
    });

    if (!currentAsset) {
      console.error(`[fetchSummary] Asset with massId ${massId} not found.`);
      return assets;
    }

    let existingDevcTypes = [];
    if (currentAsset.devices && currentAsset.devices.length > 0) {
      const uniqueTypes = new Set(currentAsset.devices.map((d) => d.devcType));
      existingDevcTypes = Array.from(uniqueTypes);
    }

    existingDevcTypes = existingDevcTypes.filter((type) => type !== 'gateway' && type !== 'limitswitch' && type !== 'power');

    if (existingDevcTypes.length === 0) {
      console.warn(`[fetchSummary] No devices found on asset ${massId}. Summary fetch skipped.`);
      return assets;
    }

    const aggregatedUnit = interval.toUpperCase();

    // Ambil ringkasan untuk SATU rentang waktu dalam 1 request dengan semua device type sekaligus.
    const fetchRange = async (rangeStart, rangeEnd) => {
      const response = await SummarySvc.getSummary({
        start: rangeStart,
        end: rangeEnd,
        aggregatedUnit,
        devcType: existingDevcTypes,
        devcMassId: massId,
        showDetails: true,
      });

      return response?.data?.data || {};
    };

    // Gabung hasil beberapa potongan ke dalam satu objek summary (per tipe device):
    // data time-series (per timestamp) di-merge, metadata summary cukup pakai
    // potongan pertama yang tersedia. Didefinisikan di luar loop (hindari no-loop-func).
    const mergeChunkInto = (target, chunk) => {
      Object.keys(chunk).forEach((devcType) => {
        const existing = target[devcType];
        if (!existing) {
          Object.assign(target, { [devcType]: chunk[devcType] });
          return;
        }
        existing.data = { ...(existing.data || {}), ...(chunk[devcType].data || {}) };
        if (!existing.summary && chunk[devcType].summary) {
          existing.summary = chunk[devcType].summary;
        }
      });
    };

    // Khusus Datalog (splitByWeek): kalau rentang > 1 minggu, request dipecah jadi
    // beberapa potongan (maks 1 minggu) yang dijalankan BERURUTAN lalu digabung.
    // Halaman lain tidak mengirim splitByWeek -> tetap 1 request (cara lama).
    const ONE_WEEK_SECONDS = 7 * 24 * 60 * 60;
    const rangeSeconds = lastQueryTimestamp - firstQueryTimestamp;

    let summaryData;
    if (splitByWeek && rangeSeconds > ONE_WEEK_SECONDS) {
      summaryData = {};
      let chunkStart = firstQueryTimestamp;
      while (chunkStart < lastQueryTimestamp) {
        const chunkEnd = Math.min(chunkStart + ONE_WEEK_SECONDS, lastQueryTimestamp);
        // eslint-disable-next-line no-await-in-loop
        const chunk = await fetchRange(chunkStart, chunkEnd);
        mergeChunkInto(summaryData, chunk);
        chunkStart = chunkEnd;
      }
    } else {
      summaryData = await fetchRange(firstQueryTimestamp, lastQueryTimestamp);
    }

    const mappedAssets = assets.map((asset) => {
      if (String(asset.massId) === String(massId)) {
        const mappedCoordinatesData = processSummarySeriesData(summaryData, firstQueryTimestamp, lastQueryTimestamp);

        const updatedAsset = {
          ...asset,
          summary: summaryData,
          coordinates: mappedCoordinatesData.map((coordinate) => coordinate.latLong),
          coordinatesData: mappedCoordinatesData,
        };

        const lastData = mappedCoordinatesData[mappedCoordinatesData.length - 1];
        if (lastData?.gpsData?.additional?.LAST_dlgpLatitude_4 && lastData?.gpsData?.additional?.LAST_dlgpLongitude_5) {
          updatedAsset.lastGpsData = lastData.gpsData;
          // eslint-disable-next-line no-undef
          updatedAsset.lastLatLng = L.latLng(lastData.gpsData?.additional?.LAST_dlgpLatitude_4, lastData.gpsData?.additional?.LAST_dlgpLongitude_5);
        }
        return updatedAsset;
      }
      return asset;
    });

    commit('SET_ASSETS', mappedAssets);
    return mappedAssets;
  },

  async fetchLastSummaryForShip({ commit, state }, massId) {
    const assets = Object.values(state.assets);
    const asset = assets.find((a) => String(a.massId) === String(massId));
    if (!asset || !asset.lastGpsData?.timestamp) return;

    const lastTimestamp = asset.lastGpsData.timestamp;
    const firstQueryTimestamp = moment((Math.floor(lastTimestamp / 3600) * 3600) * 1000).subtract(1, 'day').unix();
    const lastQueryTimestamp = moment(lastTimestamp * 1000).unix();

    const summaryResponse = await SummarySvc.getSummary({
      start: firstQueryTimestamp,
      end: lastQueryTimestamp,
      aggregatedUnit: 'HOUR',
      devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
      devcMassId: asset.massId,
      showDetails: true,
    });

    const mappedCoordinatesData = processSummarySeriesData(summaryResponse.data.data, firstQueryTimestamp, lastQueryTimestamp);

    const updatedAssets = assets.map((a) => {
      if (String(a.massId) !== String(massId)) return a;
      return {
        ...a,
        coordinates: mappedCoordinatesData.map((c) => c.latLong),
        coordinatesData: mappedCoordinatesData.slice(0, -1),
        summary: summaryResponse.data.data,
      };
    });

    commit('SET_ASSETS', updatedAssets);
  },

  async fetchAllLastSummary({ commit, state }) {
    const assets = Object.values(state.assets);
    let mappedAssets = [];

    const assetPromises = assets.map(async (asset) => {
      // fetchAllLastSummary debug logs removed

      const lastTimestamp = asset.lastGpsData?.timestamp;
      const firstQueryTimestamp = moment((Math.floor(lastTimestamp / 3600) * 3600) * 1000).subtract(1, 'day').unix();
      const lastQueryTimestamp = moment(lastTimestamp * 1000).unix();

      const summaryResponse = await SummarySvc.getSummary({
        start: firstQueryTimestamp,
        end: lastQueryTimestamp,
        aggregatedUnit: 'HOUR',
        devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
        devcMassId: asset.massId,
        showDetails: true,
      });

      const mappedCoordinatesData = processSummarySeriesData(summaryResponse.data.data, firstQueryTimestamp, lastQueryTimestamp);

      asset.coordinates = mappedCoordinatesData.map((coordinate) => coordinate.latLong);
      mappedCoordinatesData.pop();
      asset.coordinatesData = mappedCoordinatesData;
      asset.summary = summaryResponse.data.data;
      return asset;
    });

    mappedAssets = await Promise.all(assetPromises);

    commit('SET_ASSETS', mappedAssets);
    return mappedAssets;
  },

  updateAssetStatus({ commit }, payload) {
    commit('UPDATE_ASSET_STATUS', payload);
  },

  async getAssetsFromAPI({ dispatch, state }) {
    dispatch('stopLiveTracking');

    await dispatch('getAssetsData');
    await dispatch('getAssetsDevices');
    await dispatch('getLastData');

    const initialAssets = state.assets.filter((asset) => asset.lastGpsData);
    return initialAssets;
  },

  async fetchAndReconcileAssets({ dispatch, commit }) {
    const [apiAssets, visibilityMap] = await Promise.all([
      dispatch('getAssetsFromAPI'),
      getAllVisibilityStatuses(),
    ]);

    const reconciledAssets = apiAssets.map((asset) => {
      if (visibilityMap.has(asset.massId)) {
        asset.isVisible = visibilityMap.get(asset.massId);
      } else {
        asset.isVisible = true;
      }
      return asset;
    });

    commit('SET_ASSETS', reconciledAssets);

    const allStatuses = reconciledAssets.map(({ massId, isVisible }) => ({ massId, isVisible }));
    await saveAllVisibilityStatuses(allStatuses);

    return reconciledAssets;
  },

  async updateAssetVisibility({ commit }, payload) {
    commit('UPDATE_ASSET_VISIBILITY', payload);
    await saveVisibilityStatus(payload.massId, payload.isVisible);
  },

  async setAllAssetsVisibility({ commit, state }, isVisible) {
    commit('SET_ALL_ASSETS_VISIBILITY', isVisible);
    const allStatuses = state.assets.map((asset) => ({
      massId: asset.massId,
      isVisible,
    }));
    await saveAllVisibilityStatuses(allStatuses);
  },

  // Recompute every asset's icon config from the current global icon settings
  // (style + size) and drop cached Leaflet icons so maps rebuild them.
  applyIconSettings({ commit, state }) {
    const assets = state.assets.map((asset) => {
      if (!asset.shipStatus) return asset;
      const { iconConfig, markerIconUrl } = buildIconConfig(asset, asset.shipStatus);
      return {
        ...asset,
        iconConfig,
        markerIconUrl,
        icon: null,
        markerIcon: null,
        headingIcon: null,
      };
    });
    commit('SET_ASSETS', assets);
  },

  // eslint-disable-next-line no-unused-vars
  updateAssetTrail({ commit }, { massId, trail }) {
    const assets = Object.values(state.assets);

    assets.map((asset) => {
      if (asset.massId === massId) {
        asset.trail = trail;
      }
      return asset;
    });

    commit('SET_ASSETS', assets);
  },

  updateAssetDotTrail({ commit }, { massId, dotTrail }) {
    const assets = Object.values(state.assets);

    assets.map((asset) => {
      if (asset.massId === massId) {
        asset.dotTrail = dotTrail;
      }
      return asset;
    });

    commit('SET_ASSETS', assets);
  },

  resetAssetsData({ dispatch, commit }) {
    dispatch('stopLiveTracking');
    commit('SET_ASSETS', []);
    return [];
  },
};

const getters = {
  assets: (state) => state.assets,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
